import { useState } from "react";
import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, Send, RotateCcw, Circle, Loader2, Clock } from "lucide-react";
import { api } from "@/lib/api";

const STATUS_META = {
  open: {
    label: "Open",
    icon: Circle,
    className: "bg-muted text-muted-foreground border-border",
    description: "You haven't submitted this quarter yet.",
  },
  submitted: {
    label: "Submitted",
    icon: Send,
    className: "bg-accent/60 text-primary border-primary/20",
    description: "Waiting on reimbursement.",
  },
  paid: {
    label: "Paid",
    icon: CheckCircle2,
    className: "bg-primary text-primary-foreground border-primary",
    description: "You've been reimbursed.",
  },
};

function fmt(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso; }
}

export default function QuarterStatusBar({ year, quarter, status, onChanged }) {
  const [saving, setSaving] = useState(null); // "submitted" | "paid" | "open"

  const st = status?.status || "open";
  const meta = STATUS_META[st];
  const Icon = meta.icon;

  const setStatus = async (newStatus) => {
    setSaving(newStatus);
    try {
      await api.post(`/quarters/${year}/${quarter}/status`, { status: newStatus });
      const messages = {
        submitted: "Marked as submitted",
        paid: "Marked as paid",
        open: "Reopened quarter",
      };
      toast.success(messages[newStatus]);
      await onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update status");
    } finally {
      setSaving(null);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
      data-testid="quarter-status-bar"
    >
      <Card className="p-5 md:p-6 border-border/60 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className={`w-11 h-11 rounded-full flex items-center justify-center border ${meta.className}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase tracking-[0.22em] font-semibold text-muted-foreground">Reimbursement</span>
              <span
                className={`text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full border ${meta.className}`}
                data-testid="quarter-status-badge"
              >
                {meta.label}
              </span>
            </div>
            <div className="mt-1 font-display text-base md:text-lg font-medium tracking-tight">
              {meta.description}
            </div>
            {(status?.submitted_at || status?.paid_at) && (
              <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                <Clock className="w-3 h-3" />
                {st === "paid" && status?.paid_at ? `Paid on ${fmt(status.paid_at)}` : null}
                {st === "submitted" && status?.submitted_at ? `Submitted on ${fmt(status.submitted_at)}` : null}
                {st === "paid" && status?.submitted_at ? ` · submitted ${fmt(status.submitted_at)}` : null}
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 md:justify-end">
          {st === "open" && (
            <Button
              onClick={() => setStatus("submitted")}
              disabled={saving !== null}
              className="rounded-full h-9 gap-2"
              data-testid="mark-submitted-btn"
            >
              {saving === "submitted" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Mark as submitted
            </Button>
          )}
          {st === "submitted" && (
            <>
              <Button
                onClick={() => setStatus("paid")}
                disabled={saving !== null}
                className="rounded-full h-9 gap-2"
                data-testid="mark-paid-btn"
              >
                {saving === "paid" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Mark as paid
              </Button>
              <Button
                variant="outline"
                onClick={() => setStatus("open")}
                disabled={saving !== null}
                className="rounded-full h-9 gap-2"
                data-testid="reopen-btn"
              >
                <RotateCcw className="w-4 h-4" /> Reopen
              </Button>
            </>
          )}
          {st === "paid" && (
            <Button
              variant="outline"
              onClick={() => setStatus("open")}
              disabled={saving !== null}
              className="rounded-full h-9 gap-2"
              data-testid="reopen-btn"
            >
              <RotateCcw className="w-4 h-4" /> Reopen
            </Button>
          )}
        </div>
      </Card>
    </motion.div>
  );
}
